import type { LucideIcon } from 'lucide-react';

export interface StatPillProps {
  icon: LucideIcon;
  value: number | string;
  label: string;
  className?: string;
}

const cn = (...classes: Array<string | false | null | undefined>) =>
  classes.filter(Boolean).join(' ');

const trimTrailingZero = (s: string) => s.replace(/\.0$/, '');

/**
 * Popularity counters (views, clicks, followers) are shown to the public only
 * from this value up (web plan W0.6): "0 FOLLOWERS" or "3 VIEWS" reads as low
 * credibility to people and to quality raters alike.
 */
export const PUBLIC_COUNTER_MIN = 10;

/** Whether a popularity counter is high enough to show publicly. */
export function isPublicCount(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= PUBLIC_COUNTER_MIN;
}

/** Formats counters: 1234 -> "1.2K", 1200000 -> "1.2M". Strings pass through. */
export function formatStatValue(value: number | string): string {
  if (typeof value === 'string') return value;
  if (!Number.isFinite(value)) return '0';
  if (value >= 1_000_000) return `${trimTrailingZero((value / 1_000_000).toFixed(1))}M`;
  if (value >= 1_000) return `${trimTrailingZero((value / 1_000).toFixed(1))}K`;
  return String(value);
}

/** Small stat capsule: tinted icon circle + value + uppercase label. */
export function StatPill({ icon: Icon, value, label, className }: StatPillProps) {
  return (
    <div
      className={cn(
        'flex items-center gap-2.5 rounded-2xl border border-line bg-surface px-3 py-2',
        className,
      )}
    >
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10">
        <Icon size={16} className="text-primary" aria-hidden="true" />
      </span>
      <span className="flex min-w-0 flex-col">
        <span className="text-sm font-extrabold leading-tight text-ink">
          {formatStatValue(value)}
        </span>
        <span className="text-[10px] uppercase tracking-wide text-ink-secondary">
          {label}
        </span>
      </span>
    </div>
  );
}
